import { useState } from "react";

import { Button, ModalDialog } from "@ezmid/components-library";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as yup from "yup";

import {
  useCreateLessonMutation,
  EntityLessonStatus,
} from "../../../api/lesson-update.generated";
import { Optional } from "../../../enums/types";
import { useUploadMedia } from "../../../hooks";
import { makeRoute } from "../../../hooks/route";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { LessonDetailForm } from "../../lessons/molecules/LessonDetailForm";
import { AllRoutes } from "../../route/Route";

type FormValues = {
  lessonNameEN: string;
  lessonNameDE: string;
  textEn: string;
  textDe: string;
  image: string;
  order: string;
  isFree: boolean;
  status: EntityLessonStatus;
};

type Prop = {
  refetch: () => void;
  courseId: string;
  onModalClose: () => void;
  isModalOpen: boolean;
};

export function CreateLessonModal({
  refetch,
  courseId,
  onModalClose,
  isModalOpen,
}: Prop) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [addLesson] = useCreateLessonMutation();
  const [isDirtyFromImage, setIsDirtyFromImage] = useState<boolean>(false);

  const [imageFormData, setImageFormData] = useState<Optional<FormData>>(null);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(imageFormData);

  const FormSchema = yup.object().shape({
    lessonNameEN: yup.string().required(t("form.required")),
    textEn: yup.string(),
    textDe: yup.string(),
    order: yup.string().required(t("form.required")),
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    getValues,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      lessonNameEN: "",
      lessonNameDE: "",
      textEn: "",
      textDe: "",
      order: "",
      isFree: false,
      status: "ls-draft",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  const onSubmit = () => {
    const {
      order,
      lessonNameDE,
      lessonNameEN,
      textDe,
      textEn,
      isFree,
      status,
    } = getValues();
    setIsDirtyFromImage(false);
    addLesson({
      id: courseId || "",
      lessonUpdateRequest: {
        order: parseInt(order, 10),
        translations: [
          {
            language: "en",
            name: lessonNameEN,
            text: textEn,
          },
          {
            language: "de",
            name: lessonNameDE,
            text: textDe,
          },
        ],
        titleImageID: uploadedMediaId || undefined,
        isFree,
        status,
      },
    })
      .unwrap()
      .then((res) => {
        res.id &&
          navigate(
            makeRoute(AllRoutes.LESSON_DETAIL, {
              id: res.id,
            }),
            {
              state: { courseId },
            }
          );
        onModalClose();
        toast.success(t("app.lesson.add.success"));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(errorMessage || t("app.lesson.add.failed"));
        }
      });
  };

  const handleModalClose = () => {
    onModalClose();
    setImageFormData(null);
    reset({
      lessonNameEN: "",
      lessonNameDE: "",
      textEn: "",
      textDe: "",
      order: "",
      isFree: false,
      status: "ls-draft",
    });
  };

  const isImageLoading = isMediaUploading;
  const isFormDirty = isDirty || isDirtyFromImage;
  return (
    <ModalDialog
      id="create-lesson-modal"
      title={t("app.lesson.create")}
      onRequestClose={handleModalClose}
      width="54%"
      isOpen={isModalOpen}
      overlayStyles={{
        zIndex: 1000,
        overflow: "visible",
      }}
      dialogStyles={{
        zIndex: 1000,
        overflow: "auto",
        maxHeight: "90vh",
      }}
      footerControls={
        <>
          <Button type="button" onClick={handleModalClose}>
            {t("button.close")}
          </Button>
          <div className="pl-2">
            <Button
              onClick={onSubmit}
              disabled={!isFormDirty || isImageLoading}
            >
              {t("button.save")}
            </Button>
          </div>
        </>
      }
    >
      <LessonDetailForm<FormValues>
        control={control}
        errors={errors}
        handleSubmit={handleSubmit(onSubmit)}
        isSubmitButtonDisabled={!isFormDirty || isImageLoading}
        onSumbit={onSubmit}
      />
    </ModalDialog>
  );
}
